/**
 * CRM > Accounts. Wholesale accounts synced from Sage, with realized-order and
 * open-pipeline aggregates from the crm_account_rollup view.
 *
 * One row per account. Click a row to open the detail (contacts, opportunities,
 * recent orders) and edit the three CRM-owned fields (owner, tier, status).
 * Structure mirrors the Finance > Invoices grid.
 */

import { useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { Pill } from '@/components/Pill';
import { ClearableSearchInput } from '@/components/ClearableSearchInput';
import { SortableHeader, gridTheadClassName, type SortDir } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { useSession } from '@/lib/useSession';
import { formatDateShort } from '@/lib/dateUtils';
import {
  accountStatusLabel,
  accountStatusTone,
  formatCrmMoney,
  hasCrmAccounts,
  tierLabel,
  tierTone,
  useCrmAccounts,
  useCrmReps,
  ACCOUNT_STATUS_OPTIONS,
  ACCOUNT_TIER_OPTIONS,
  type AccountFilters,
  type AccountRow,
} from '@/lib/useCrm';

// Client-side sort over the rows already loaded (the accounts list endpoint
// keyset-orders by name; the chosen sort reorders whatever is in hand, exactly
// like the Invoices grid). Mirrors parseInvoiceSortParam.
type AccountSortColumn =
  | 'name'
  | 'tier'
  | 'owner'
  | 'open_order_amount'
  | 'weighted_pipeline'
  | 'last_order_date'
  | 'status';

const SORTABLE_ACCOUNT_COLUMNS: ReadonlySet<AccountSortColumn> = new Set<AccountSortColumn>([
  'name',
  'tier',
  'owner',
  'open_order_amount',
  'weighted_pipeline',
  'last_order_date',
  'status',
]);

function parseAccountSort(raw: string | null): { column: AccountSortColumn; dir: SortDir } | null {
  if (!raw) return null;
  const [col, dir] = raw.split(':');
  if (!col || !dir) return null;
  if (!SORTABLE_ACCOUNT_COLUMNS.has(col as AccountSortColumn)) return null;
  if (dir !== 'asc' && dir !== 'desc') return null;
  return { column: col as AccountSortColumn, dir };
}

function sortValue(
  row: AccountRow,
  col: AccountSortColumn,
  repName: (id: number | null) => string,
): string | number {
  switch (col) {
    case 'name': return row.name.toLowerCase();
    case 'tier': return row.tier;
    case 'owner': return repName(row.owner_rep_id).toLowerCase();
    case 'open_order_amount': return Number(row.open_order_amount ?? 0);
    case 'weighted_pipeline': return Number(row.weighted_pipeline ?? 0);
    case 'last_order_date': return row.last_order_date ?? '';
    case 'status': return row.status;
  }
}

function sortRows(
  rows: AccountRow[],
  col: AccountSortColumn,
  dir: SortDir,
  repName: (id: number | null) => string,
): AccountRow[] {
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, col, repName);
    const bv = sortValue(b, col, repName);
    if (av < bv) return -1 * factor;
    if (av > bv) return 1 * factor;
    return 0;
  });
}

export function CrmAccounts() {
  const { user } = useSession();
  const canRead = hasCrmAccounts(user?.permissions, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const tier = searchParams.get('tier') ?? '';
  const status = searchParams.get('status') ?? '';
  const owner = searchParams.get('owner') ?? '';

  const update = useCallback(
    (mutate: (sp: URLSearchParams) => void) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );
  const setStr = (k: string, v: string) => update((sp) => (v ? sp.set(k, v) : sp.delete(k)));
  const resetFilters = () => update((sp) => ['q', 'tier', 'status', 'owner', 'sort'].forEach((k) => sp.delete(k)));

  const filters: AccountFilters = useMemo(
    () => ({
      q: q || undefined,
      tier: tier || undefined,
      status: status || undefined,
      owner_rep_id: owner ? Number(owner) : undefined,
    }),
    [q, tier, status, owner],
  );

  const accountsQ = useCrmAccounts(filters);
  const { reps } = useCrmReps();
  const repName = useCallback(
    (id: number | null): string => (id == null ? '' : reps.find((r) => r.id === id)?.name ?? `Rep ${id}`),
    [reps],
  );

  const sort = parseAccountSort(searchParams.get('sort'));
  const toggleSort = (col: AccountSortColumn) => {
    if (!sort || sort.column !== col) update((sp) => sp.set('sort', `${col}:asc`));
    else if (sort.dir === 'asc') update((sp) => sp.set('sort', `${col}:desc`));
    else update((sp) => sp.delete('sort'));
  };
  const sortedFor = (col: AccountSortColumn): SortDir | null =>
    sort && sort.column === col ? sort.dir : null;

  if (user && !canRead) return <AccessDeniedCard />;

  const rows = sort ? sortRows(accountsQ.rows, sort.column, sort.dir, repName) : accountsQ.rows;

  const filterBar = (
    <div className="flex flex-wrap gap-3 items-end">
      <div className="flex-1 min-w-[220px]">
        <label htmlFor="q" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Search</label>
        <ClearableSearchInput
          id="q"
          value={q}
          onChange={(v) => setStr('q', v)}
          placeholder="Account name or Sage customer no..."
          className="w-full border border-amp-line rounded pl-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
        />
      </div>
      <div>
        <label htmlFor="tier" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Tier</label>
        <select
          id="tier"
          value={tier}
          onChange={(e) => setStr('tier', e.target.value)}
          className="min-w-[130px] border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          <option value="">All</option>
          {ACCOUNT_TIER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="owner" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Owner</label>
        <select
          id="owner"
          value={owner}
          onChange={(e) => setStr('owner', e.target.value)}
          className="min-w-[150px] border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          <option value="">All</option>
          {reps.map((r) => (
            <option key={r.id} value={String(r.id)}>{r.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="status" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Status</label>
        <select
          id="status"
          value={status}
          onChange={(e) => setStr('status', e.target.value)}
          className="min-w-[130px] border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          <option value="">All</option>
          {ACCOUNT_STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      {(q || tier || status || owner) && (
        <button
          type="button"
          onClick={resetFilters}
          className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
        >
          Reset filters
        </button>
      )}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">CRM</p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Accounts</h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Wholesale accounts synced from Sage, with open orders and weighted pipeline.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ToolPinToggle toolSlug="accounts" toolName="Accounts" />
        </div>
      </div>

      <DataGridShell
        total={accountsQ.totalCount}
        loaded={rows.length}
        countNoun="accounts"
        toolbar={filterBar}
        onReachEnd={accountsQ.fetchNextPage}
        hasMore={accountsQ.hasNextPage}
        isLoadingMore={accountsQ.isFetchingNextPage}
      >
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <SortableHeader label="Account" sorted={sortedFor('name')} onToggle={() => toggleSort('name')} thClassName="px-3 py-2" />
              <SortableHeader label="Tier" sorted={sortedFor('tier')} onToggle={() => toggleSort('tier')} thClassName="px-3 py-2 w-[110px]" />
              <SortableHeader label="Owner" sorted={sortedFor('owner')} onToggle={() => toggleSort('owner')} thClassName="px-3 py-2 w-[150px]" />
              <SortableHeader label="Open Orders" sorted={sortedFor('open_order_amount')} onToggle={() => toggleSort('open_order_amount')} align="right" thClassName="px-3 py-2 w-[120px]" />
              <SortableHeader label="Weighted Pipeline" sorted={sortedFor('weighted_pipeline')} onToggle={() => toggleSort('weighted_pipeline')} align="right" thClassName="px-3 py-2 w-[150px]" />
              <SortableHeader label="Last Order" sorted={sortedFor('last_order_date')} onToggle={() => toggleSort('last_order_date')} thClassName="px-3 py-2 w-[100px]" />
              <SortableHeader label="Status" sorted={sortedFor('status')} onToggle={() => toggleSort('status')} thClassName="px-3 py-2 w-[110px]" />
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {accountsQ.isLoading ? (
              <tr><td colSpan={7} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading accounts...</td></tr>
            ) : accountsQ.error ? (
              <tr><td colSpan={7} className="px-4 py-6 text-red-700 dark:text-red-300">Could not load: {accountsQ.error.message}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">No accounts match this filter.</td></tr>
            ) : (
              rows.map((row) => <AccountListRow key={row.id} row={row} ownerName={repName(row.owner_rep_id)} />)
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}

function AccountListRow({ row, ownerName }: { row: AccountRow; ownerName: string }) {
  const detailHref = {
    pathname: `/crm/accounts/${row.id}`,
    search: typeof window !== 'undefined' ? window.location.search : '',
  };
  return (
    <tr className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
        <Link to={detailHref} className="hover:underline">{row.name}</Link>
        <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted font-mono">{row.sage_customer_no}</div>
      </td>
      <td className="px-3 py-2"><Pill tone={tierTone(row.tier)}>{tierLabel(row.tier)}</Pill></td>
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{ownerName || '--'}</td>
      <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">{formatCrmMoney(row.open_order_amount)}</td>
      <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">{formatCrmMoney(row.weighted_pipeline)}</td>
      <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{formatDateShort(row.last_order_date)}</td>
      <td className="px-3 py-2"><Pill tone={accountStatusTone(row.status)}>{accountStatusLabel(row.status)}</Pill></td>
    </tr>
  );
}

function AccessDeniedCard() {
  return (
    <div className="max-w-xl rounded-lg border border-amp-line p-5 dark:border-amp-dark-line">
      <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">No access</h2>
      <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
        CRM is gated by the crm:read capability. Ask IT to grant you access.
      </p>
    </div>
  );
}
